import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { updateProductMetaData } from "@containers/App/reducer.js"
import { setCookie, eraseCookie } from "@commonUtils"
import { parsePageData } from "./parser"
import { COMMON_ERROR_CONFIG, LINK_EXPIRED_ERROR_CONFIG, LOGIN_ERROR_CONFIG } from "../constants"
import { pushToGTM } from "@lib/gtmUtility"

const initialState = {
    isFetching: false,
    isFetched: false,
    data: null,
    error: null,
    verifyPhone: {
        isFetching: false,
        isFetched: false,
        error: null,
    },
}

export const selfServeLoginPageSlice = createSlice({
    name: "selfServeLoginPageReducer",
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        //handle asynchronous actions
        builder.addCase(fetchLoginPageData.fulfilled, (state, action) => {
            state.data = parsePageData(action?.payload, state.labsBookingPageData, state.formData)
            state.isFetched = true
            state.isFetching = false
            state.error = null
        }),
            builder.addCase(fetchLoginPageData.pending, (state) => {
                state.isFetching = true
                state.isFetched = false
                state.error = null
                state.data = null
            }),
            builder.addCase(fetchLoginPageData.rejected, (state, action) => {
                state.isFetching = false
                state.isFetched = true
                state.data = null
                if (!action.payload?.status_code) {
                    state.error = COMMON_ERROR_CONFIG
                } else if (action.payload?.status_code === 410) {
                    state.error = LINK_EXPIRED_ERROR_CONFIG
                } else {
                    state.error = LOGIN_ERROR_CONFIG
                }
            })
        builder.addCase(verifySelfServeDetails.fulfilled, (state, action) => {
            state.verifyPhone.isFetched = true
            state.verifyPhone.isFetching = false
            state.verifyPhone.error = false
            const { navigate } = action.meta.arg
            if (action.payload) {
                navigate(action.payload)
            }
        }),
            builder.addCase(verifySelfServeDetails.pending, (state) => {
                state.verifyPhone.isFetching = true
                state.verifyPhone.error = null
            }),
            builder.addCase(verifySelfServeDetails.rejected, (state, action) => {
                state.verifyPhone.isFetching = false
                state.verifyPhone.error = action.payload
            })
    },
})

/**
 *
 * @description Verify Self Serve Phone number
 */
export const verifySelfServeDetails = createAsyncThunk(
    "selfServeLoginPageReducer/verifyPhone",
    async (_arg, { extra, rejectWithValue }) => {
        const { ppmcApi } = extra
        const { uuid, phoneNumber, gaDetails } = _arg
        const payload = {
            mobile: phoneNumber,
        }
        eraseCookie("selfServeUUID")
        eraseCookie("selfServeToken")
        eraseCookie("selfServeReschedule")
        try {
            const res = await ppmcApi.post("/v6/self-serve/auth", payload, {
                headers: { uuid },
            })
            if (res?.data) {
                setCookie("selfServeUUID", res.data?.uuid)
                setCookie("selfServeToken", res.data?.token)
                return Promise.resolve(res.data?.redirect_url)
            }
        } catch (error) {
            if (gaDetails) pushToGTM(gaDetails)
            return rejectWithValue(
                error?.message || error?.error?.message || "Something went wrong. Please try again"
            )
        }
    }
)

/**
 * @description Fetch Page Data for Self Serve Login page
 */
export const fetchLoginPageData = createAsyncThunk(
    "selfServeLoginPageReducer/fetchData",
    async (_arg, { extra, dispatch, rejectWithValue }) => {
        const { uuid = "" } = _arg
        const { ppmcApi } = extra
        try {
            if (!uuid) {
                return rejectWithValue("uuid not found")
            }
            const res = await ppmcApi.get(`/v6/self-serve/form-structure?form_name=self_serve_login_page`, {
                headers: { uuid },
            })
            if (res?.data?.meta_data) {
                dispatch(
                    updateProductMetaData({
                        title: res.data.meta_data?.title,
                        description: res.data.meta_data?.description,
                    })
                )
            }
            return Promise.resolve(res.data)
        } catch (error) {
            return rejectWithValue(error)
        }
    }
)

export const selfServeLoginPageReducer = selfServeLoginPageSlice.reducer
